import { HSLColor, generateAccentPalette } from '@yola/yola-palette-generator';
import { rules } from '@yola/ws-color-rules';
import parseHslColorString from './parse-hsl-color-string';
import {
  getPrimaryButtonVariableNames,
  getSecondaryButtonVariableNames,
} from './get-button-variable-names';
import getColorFromVariables from './get-color-from-variables';
import expandPrimaryButtonVariables from './expand-primary-button-variables';
import expandSecondaryButtonVariables from './expand-secondary-button-variables';
import constants from '../constants';
import { variableVariants } from '../constants/variable-names';
import overrideStrategies from '../constants/variable-override-strategies';
import getAdvancedColorVariableDescriptors from './get-advanced-color-variable-descriptors';
import stringifyGradientModel from './stringify-gradient-model';

const expandToAllPresets = (colorMap, expander) =>
  Object.fromEntries(
    Object.entries(colorMap)
      .map(([variableName, value]) => expander(variableName).map((name) => [name, value]))
      .flat()
  );

const parseToHSLColor = (colorString) => {
  const { h, s, l, a } = parseHslColorString(colorString);
  return new HSLColor(h, s, l, a);
};

const buildLinkOverrides = ({
  variableName,
  variants,
  targetColor,
  targetColorPalette,
  whiteColor,
  blackColor,
}) => ({
  [variableName]: targetColor,
  [variants.HOVER]: rules.link.hover.custom({
    targetColor,
    targetColorPalette,
    whiteColor,
    blackColor,
  }),
});

const buildInputOverrides = ({ variableName, variants, targetColor, backgroundColor }) => ({
  [variableName]: targetColor,
  [variants.DISABLED]: rules.input.borderColor.disabled.custom({
    targetColor,
    backgroundColor,
  }),
  [variants.CHECKBOX_BORDER]: targetColor,
  [variants.DISABLED_CHECKBOX_BORDER]: rules.checkbox.switchedOff.borderColor.disabled.custom({
    targetColor,
    backgroundColor,
  }),
});

const buildSocialIconOverrides = ({
  variableName,
  variants,
  targetColor,
  targetColorPalette,
  whiteColor,
  blackColor,
}) => ({
  [variableName]: targetColor,
  [variants.HOVER]: rules.icon.social.hover.custom({
    targetColor,
    targetColorPalette,
    whiteColor,
    blackColor,
  }),
});

const buildPrimaryButtonSurfaceOverrides = ({
  variableName,
  variants,
  targetColor,
  targetColorPalette,
  whiteColor,
  blackColor,
  backgroundColor,
}) =>
  expandToAllPresets(
    {
      [variableName]: targetColor,
      [variants.HOVER]: rules.primaryButton.brandedSolid.background.hover.custom({
        targetColor,
        targetColorPalette,
        whiteColor,
        blackColor,
        backgroundColor,
      }),
      [variants.ACTIVE]: rules.primaryButton.brandedSolid.background.active.custom({
        targetColor,
        targetColorPalette,
        whiteColor,
        blackColor,
        backgroundColor,
      }),
      [variants.DISABLED]: rules.primaryButton.brandedSolid.background.disabled.custom({
        targetColor,
        backgroundColor,
      }),
    },
    expandPrimaryButtonVariables
  );

const buildPrimaryButtonTextOverrides = ({
  variableName,
  variants,
  targetColor,
  buttonBackgroundColor,
  backgroundColor,
}) =>
  expandToAllPresets(
    {
      [variableName]: targetColor,
      [variants.HOVER]: targetColor,
      [variants.ACTIVE]: targetColor,
      [variants.DISABLED]: rules.primaryButton.brandedSolid.text.disabled.custom({
        targetColor,
        backgroundColor: rules.primaryButton.brandedSolid.background.disabled.custom({
          targetColor: buttonBackgroundColor,
          backgroundColor,
        }),
      }),
    },
    expandPrimaryButtonVariables
  );

const buildSecondaryButtonSurfaceOverrides = ({
  variableName,
  variants,
  targetColor,
  backgroundColor,
}) =>
  expandToAllPresets(
    {
      [variableName]: targetColor,
      [variants.HOVER]: rules.secondaryButton.lightGray.background.hover.custom({
        targetColor,
      }),
      [variants.ACTIVE]: rules.secondaryButton.lightGray.background.active.custom({
        targetColor,
      }),
      [variants.DISABLED]: rules.secondaryButton.lightGray.background.disabled.custom({
        targetColor,
        backgroundColor,
      }),
    },
    expandSecondaryButtonVariables
  );

const buildSecondaryButtonTextOverrides = ({
  variableName,
  variants,
  targetColor,
  backgroundColor,
  buttonBackgroundColor,
}) =>
  expandToAllPresets(
    {
      [variableName]: targetColor,
      [variants.HOVER]: targetColor,
      [variants.ACTIVE]: targetColor,
      [variants.DISABLED]: rules.secondaryButton.lightGray.text.disabled.custom({
        targetColor,
        backgroundColor: rules.secondaryButton.lightGray.background.disabled.custom({
          targetColor: buttonBackgroundColor,
          backgroundColor,
        }),
      }),
    },
    expandSecondaryButtonVariables
  );

export const buildBlockBackgroundLayerOverrides = ({
  variableName,
  hsl: { h, s, l },
  gradientModel,
}) => ({
  [variableName]: new HSLColor(h, s, l, gradientModel ? 0 : 1),
  [variableVariants[variableName].GRADIENT]: gradientModel
    ? stringifyGradientModel(gradientModel)
    : 'none',
});

const getAdvancedColorsToOverride = ({
  hsla: { h, s, l, a },
  variableName,
  backgroundColor,
  variables,
  buttonPreset,
  gradientModel = null,
}) => {
  // randomly ColorPicker return `a` as NaN, to prevent crash we setup fallback value
  const opacity = isNaN(a) ? constants.MAX_OPACITY_VALUE : a;
  const targetColor = new HSLColor(h, s, l, opacity);
  const targetColorOpaque = new HSLColor(h, s, l);
  const descriptor = getAdvancedColorVariableDescriptors(buttonPreset).find(
    (item) => item.variableName === variableName
  );

  const builderContext = {
    variableName,
    targetColor,
    targetColorPalette: generateAccentPalette(targetColorOpaque),
    whiteColor: new HSLColor(0, 0, 1),
    blackColor: new HSLColor(0, 0, 0),
    backgroundColor: parseToHSLColor(backgroundColor),
  };

  const getPrimaryBtnContext = () => {
    const [primaryBtnVariableNames, primaryBtnVariableVariants] =
      getPrimaryButtonVariableNames(buttonPreset);
    const buttonBackgroundColor = parseToHSLColor(
      `hsl(${getColorFromVariables(
        `${primaryBtnVariableNames.PRIMARY_BUTTON_BACKGROUND}_color`,
        variables
      )})`
    );
    return { variants: primaryBtnVariableVariants[variableName], buttonBackgroundColor };
  };

  const getSecondaryBtnContext = () => {
    const [secondaryBtnVariableNames, secondaryBtnVariableVariants] =
      getSecondaryButtonVariableNames(buttonPreset);
    const buttonBackgroundColor = parseToHSLColor(
      `hsl(${getColorFromVariables(
        `${secondaryBtnVariableNames.SECONDARY_BUTTON_BACKGROUND}_color`,
        variables
      )})`
    );
    return { variants: secondaryBtnVariableVariants[variableName], buttonBackgroundColor };
  };

  switch (descriptor?.overrideStrategy) {
    case overrideStrategies.SIMPLE_HSL:
      return { [variableName]: targetColorOpaque };
    case overrideStrategies.SIMPLE_HSLA:
      return { [variableName]: targetColor };
    case overrideStrategies.BLOCK_BACKGROUND_LAYER:
      return buildBlockBackgroundLayerOverrides({
        variableName,
        hsl: { h, s, l },
        gradientModel,
      });
    case overrideStrategies.LINK:
      return buildLinkOverrides({ ...builderContext, variants: variableVariants[variableName] });
    case overrideStrategies.INPUT_BORDER:
      return buildInputOverrides({ ...builderContext, variants: variableVariants[variableName] });
    case overrideStrategies.SOCIAL_ICON:
      return buildSocialIconOverrides({
        ...builderContext,
        variants: variableVariants[variableName],
      });
    case overrideStrategies.PRIMARY_BUTTON_SURFACE:
      return buildPrimaryButtonSurfaceOverrides({
        ...builderContext,
        variants: getPrimaryBtnContext().variants,
      });
    case overrideStrategies.PRIMARY_BUTTON_TEXT:
      return buildPrimaryButtonTextOverrides({
        ...builderContext,
        ...getPrimaryBtnContext(),
      });
    case overrideStrategies.SECONDARY_BUTTON_SURFACE:
      return buildSecondaryButtonSurfaceOverrides({
        ...builderContext,
        variants: getSecondaryBtnContext().variants,
      });
    case overrideStrategies.SECONDARY_BUTTON_TEXT:
      return buildSecondaryButtonTextOverrides({
        ...builderContext,
        ...getSecondaryBtnContext(),
      });
    default:
      if (!descriptor) {
        // eslint-disable-next-line no-console
        console.warn(`No descriptor found for variable: ${variableName}`);
      }
      return { [variableName]: targetColor };
  }
};

export default getAdvancedColorsToOverride;
